import palette from "assets/theme/base/palette";
import React from "react";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

/**
 * Reusable confirmation dialog — styled like ShareContentDialog.
 *
 * Props:
 *  open          – boolean
 *  onClose       – () => void
 *  onConfirm     – () => void
 *  title         – string
 *  message       – string | ReactNode
 *  confirmLabel  – string  (default "Confirm")
 *  cancelLabel   – string  (default "Cancel")
 *  loading       – boolean
 *  dangerous     – boolean (red theme)
 *  icon          – Material icon name (default "help_outline")
 *  iconColor     – CSS color override (optional)
 */
const ConfirmDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
  dangerous = false,
  icon = "help_outline",
  iconColor,
}) => {
  const actionColor = iconColor || (dangerous ? "#f44336" : palette.brand[500]);
  const actionBg    = dangerous ? "#fdecea" : palette.brand[50];
  const hoverColor  = dangerous ? "#d32f2f" : palette.brand[600];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: "24px", overflowX: "hidden" } }}
    >
      {/* Close button */}
      <MDBox
        onClick={onClose}
        sx={{
          position: "absolute", top: 12, right: 12, zIndex: 10,
          width: 36, height: 36, borderRadius: "50%",
          bgcolor: palette.neutral[100],
          border: `1px solid ${palette.neutral[200]}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer",
          transition: "all 180ms ease",
          "&:hover": { bgcolor: palette.neutral[200] },
        }}
      >
        <Icon sx={{ fontSize: "18px !important", color: palette.neutral[600] }}>close</Icon>
      </MDBox>

      <MDBox sx={{ px: 4, pt: 5, pb: 4, textAlign: "center" }}>
        {/* Decorative icon with sparkles */}
        <MDBox sx={{ position: "relative", display: "inline-flex", mb: 3 }}>
          <MDBox sx={{ position: "absolute", top: -10, left: -18, color: actionColor, fontSize: "18px", lineHeight: 1 }}>✦</MDBox>
          <MDBox sx={{ position: "absolute", bottom: 0, left: -12, color: "#f48326", fontSize: "10px", lineHeight: 1 }}>●</MDBox>
          <MDBox sx={{ position: "absolute", top: 2, right: -20, color: "#9c27b0", fontSize: "10px", lineHeight: 1 }}>●</MDBox>
          <MDBox sx={{ position: "absolute", bottom: -8, right: -16, color: actionColor, fontSize: "14px", lineHeight: 1 }}>✦</MDBox>
          <MDBox sx={{
            width: 100, height: 100, borderRadius: "50%",
            bgcolor: actionBg,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon sx={{ fontSize: "48px !important", color: actionColor }}>{icon}</Icon>
          </MDBox>
        </MDBox>

        {/* Title */}
        <MDTypography variant="h5" fontWeight="bold" sx={{ color: "text.primary", mb: 1 }}>
          {title}
        </MDTypography>

        <Divider sx={{ width: 40, mx: "auto", mb: 2, borderBottomWidth: "2px", borderColor: palette.neutral[300] }} />

        {/* Message */}
        {message && (
          <MDTypography variant="body2" sx={{ color: "text.secondary", mb: 3, lineHeight: 1.7 }}>
            {message}
          </MDTypography>
        )}

        {/* Confirm button */}
        <MDButton
          onClick={onConfirm}
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            borderRadius: "12px", py: 1.5, mb: 1.5, fontSize: "0.85rem",
            letterSpacing: "0.06em",
            bgcolor: actionColor, color: "#fff",
            "&:hover": { bgcolor: hoverColor },
            "&.Mui-disabled": { bgcolor: palette.neutral[300], color: "#fff" },
          }}
        >
          {loading
            ? <CircularProgress size={18} sx={{ color: "#fff" }} />
            : <><Icon sx={{ mr: 1, fontSize: "18px !important" }}>{icon}</Icon>{confirmLabel}</>
          }
        </MDButton>

        {/* Cancel button */}
        <MDButton
          onClick={onClose}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: "12px", py: 1.5, fontSize: "0.85rem",
            letterSpacing: "0.06em", borderColor: palette.neutral[300],
            color: palette.neutral[600],
            "&:hover": { bgcolor: palette.neutral[100], borderColor: palette.neutral[400] },
          }}
        >
          {cancelLabel}
        </MDButton>
      </MDBox>
    </Dialog>
  );
};

export default ConfirmDialog;
